import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import toast from 'react-hot-toast';

/**
 * V3 Marketplace Page - Black & White Premium Theme
 * NFT Listings + Buy/Sell Functions + Order Management
 */
const MarketplaceV3 = () => {
  const { t } = useTranslation();
  const {
    account,
    contracts,
    balances,
    connectWallet,
    isConnecting,
    getActiveOrders,
    createOrderUSDT,
    createOrderETH,
    buyWithUSDT,
    buyWithETH,
    cancelOrder,
    getUserNFTs,
    getRecentTrades
  } = useWeb3V3();

  // State management
  const [loading, setLoading] = useState(true);
  const [activeOrders, setActiveOrders] = useState([]);
  const [myNFTs, setMyNFTs] = useState([]);
  const [nftDetailsMap, setNftDetailsMap] = useState({}); // tokenId -> NFT details
  const [recentTrades, setRecentTrades] = useState([]); // Recent trades
  const [selectedTab, setSelectedTab] = useState('market'); // 'market' | 'my-orders' | 'trades'
  const [filterPayment, setFilterPayment] = useState('all'); // 'all' | 'USDT' | 'ETH'
  const [searchNumber, setSearchNumber] = useState('');
  const [processing, setProcessing] = useState(false);

  // Create order Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [orderPrice, setOrderPrice] = useState('');
  const [orderPaymentMethod, setOrderPaymentMethod] = useState('USDT');

  // Load data
  useEffect(() => {
    if (account && contracts.marketplace) {
      loadMarketData();
    }
  }, [account, contracts.marketplace]);

  // Get single NFT metadata
  const fetchNFTMetadata = async (tokenId) => {
    if (!contracts.lotteryNFT) return null;
    try {
      const metadata = await contracts.lotteryNFT.getMetadata(tokenId);
      return {
        tokenId: tokenId.toString(),
        number: metadata.number.toString().padStart(6, '0'),
        signature: metadata.signature,
        mintTimestamp: metadata.mintTimestamp.toString(),
        drawId: metadata.currentDrawId.toString(),
        exists: metadata.exists
      };
    } catch (error) {
      console.error(`Failed to get NFT ${tokenId} metadata:`, error);
      return null;
    }
  };

  const loadMarketData = async () => {
    try {
      setLoading(true);

      const [orders, nfts, trades] = await Promise.all([
        getActiveOrders(),
        getUserNFTs(),
        getRecentTrades ? getRecentTrades(10) : []
      ]);

      setActiveOrders(orders || []);
      setMyNFTs(nfts || []);
      setRecentTrades(trades || []);

      // Get NFT metadata for all orders
      const detailsMap = {};

      // First add user's own NFTs to map
      for (const nft of (nfts || [])) {
        detailsMap[nft.tokenId] = nft;
      }

      // Get NFT metadata from market orders (if not in user's NFTs)
      for (const order of (orders || [])) {
        if (!detailsMap[order.tokenId]) {
          const metadata = await fetchNFTMetadata(order.tokenId);
          if (metadata) {
            detailsMap[order.tokenId] = metadata;
          }
        }
      }

      // Get NFT metadata from recent trade records
      for (const trade of (trades || [])) {
        if (!detailsMap[trade.tokenId]) {
          const metadata = await fetchNFTMetadata(trade.tokenId);
          if (metadata) {
            detailsMap[trade.tokenId] = metadata;
          }
        }
      }

      setNftDetailsMap(detailsMap);

    } catch (error) {
      console.error('Failed to load market data:', error);
      toast.error(t('marketplace.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Filter orders
  const filteredOrders = activeOrders.filter((order) => {
    // Payment method filter
    if (filterPayment !== 'all' && order.paymentMethod !== filterPayment) {
      return false;
    }

    // Number search
    if (searchNumber) {
      const nft = nftDetailsMap[order.tokenId];
      if (!nft || !nft.number.includes(searchNumber)) {
        return false;
      }
    }

    return true;
  });

  // My orders
  const myOrders = activeOrders.filter((order) =>
    order.seller.toLowerCase() === account?.toLowerCase()
  );

  // Get NFT details (from cached map)
  const getNFTDetails = (tokenId) => {
    return nftDetailsMap[tokenId] || null;
  };

  // Open create order Modal
  const handleCreateOrder = (nft) => {
    setSelectedNFT(nft);
    setOrderPrice('');
    setOrderPaymentMethod('USDT');
    setShowCreateModal(true);
  };

  // Submit create order
  const handleSubmitOrder = async () => {
    if (!selectedNFT || !orderPrice) {
      toast.error(t('marketplace.error.enterPrice'));
      return;
    }

    const price = parseFloat(orderPrice);
    if (isNaN(price) || price <= 0) {
      toast.error(t('marketplace.error.invalidPrice'));
      return;
    }

    try {
      setProcessing(true);

      if (orderPaymentMethod === 'USDT') {
        await createOrderUSDT(selectedNFT.tokenId, price);
      } else {
        await createOrderETH(selectedNFT.tokenId, price);
      }

      // Refresh data
      await loadMarketData();

      // Close Modal
      setShowCreateModal(false);
      setSelectedNFT(null);
      setOrderPrice('');

    } catch (error) {
      console.error('Failed to create order:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Buy NFT
  const handleBuy = async (order) => {
    if (!account) {
      toast.error(t('marketplace.error.connectWallet'));
      return;
    }

    // Check balance
    const price = parseFloat(order.price);
    const balance = order.paymentMethod === 'USDT'
      ? parseFloat(balances.usdt)
      : parseFloat(balances.eth);

    if (balance < price) {
      toast.error(`${order.paymentMethod}${t('marketplace.error.insufficientBalance')}`);
      return;
    }

    try {
      setProcessing(true);

      if (order.paymentMethod === 'USDT') {
        await buyWithUSDT(order.orderId);
      } else {
        await buyWithETH(order.orderId);
      }

      // Refresh data
      await loadMarketData();

    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Cancel order
  const handleCancel = async (orderId) => {
    try {
      setProcessing(true);
      await cancelOrder(orderId);
      // Refresh data
      await loadMarketData();
    } catch (error) {
      console.error('Failed to cancel order:', error);
    } finally {
      setProcessing(false);
    }
  };

  // If wallet not connected
  if (!account) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-6">{t('marketplace.title')}</h2>
          <p className="text-gray-400 mb-8">{t('marketplace.subtitle')}</p>
          <button
            onClick={connectWallet}
            disabled={isConnecting}
            className="btn-primary px-8 py-3"
          >
            {isConnecting ? t('common.connecting') : t('common.connectWallet')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container-v3">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-5xl font-black mb-2">{t('marketplace.title')}</h1>
          <p className="text-gray-400">
            {t('marketplace.subtitle')}
          </p>
        </div>

        {/* Statistics cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('marketplace.onSale')}</div>
            <div className="text-4xl font-bold font-mono">{filteredOrders.length}</div>
          </div>
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('marketplace.myOrders')}</div>
            <div className="text-4xl font-bold font-mono">{myOrders.length}</div>
          </div>
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('marketplace.usdtBalance')}</div>
            <div className="text-2xl font-bold font-mono">{parseFloat(balances.usdt).toFixed(2)}</div>
          </div>
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('marketplace.ethBalance')}</div>
            <div className="text-2xl font-bold font-mono">{parseFloat(balances.eth).toFixed(4)}</div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="border-b border-gray-800 mb-8">
          <div className="flex gap-8">
            <button
              onClick={() => setSelectedTab('market')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'market'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('marketplace.tabs.market')} ({filteredOrders.length})
            </button>
            <button
              onClick={() => setSelectedTab('my-orders')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'my-orders'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('marketplace.tabs.myOrders')} ({myOrders.length})
            </button>
            <button
              onClick={() => setSelectedTab('trades')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'trades'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('marketplace.tabs.recentTrades') || 'Recent Trades'} ({recentTrades.length})
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {selectedTab === 'market' && (
          <div className="mb-6 flex flex-col md:flex-row gap-4">
            {/* Payment method filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilterPayment('all')}
                className={`px-4 py-2 border-2 transition-all ${
                  filterPayment === 'all'
                    ? 'border-white bg-white text-black'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                {t('marketplace.filters.all')}
              </button>
              <button
                onClick={() => setFilterPayment('USDT')}
                className={`px-4 py-2 border-2 transition-all ${
                  filterPayment === 'USDT'
                    ? 'border-white bg-white text-black'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                 USDT
              </button>
              <button
                onClick={() => setFilterPayment('ETH')}
                className={`px-4 py-2 border-2 transition-all ${
                  filterPayment === 'ETH'
                    ? 'border-white bg-white text-black'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                 ETH
              </button>
            </div>

            {/* Number search */}
            <input
              type="text"
              value={searchNumber}
              onChange={(e) => setSearchNumber(e.target.value)}
              placeholder={t('marketplace.filters.searchPlaceholder')}
              className="input flex-1"
            />
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="text-gray-500 text-xl">{t('common.loading')}</div>
          </div>
        ) : (
          <>
            {/* Market list */}
            {selectedTab === 'market' && (
              <div>
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-20">
                    <h3 className="text-2xl font-bold mb-4">{t('marketplace.empty')}</h3>
                    <p className="text-gray-400">{t('marketplace.emptyDesc')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredOrders.map((order) => {
                      const nft = getNFTDetails(order.tokenId);
                      const isMyOrder = order.seller.toLowerCase() === account.toLowerCase();

                      return (
                        <div key={order.orderId} className="card p-6 hover:border-white transition-all">
                          {/* NFT number */}
                          <div className="text-center mb-4">
                            <div className="text-sm text-gray-500 mb-2">NFT #{order.tokenId}</div>
                            <div className="text-5xl font-black font-mono tracking-wider mb-2">
                              {nft?.number || '------'}
                            </div>
                          </div>

                          {/* Signature */}
                          {nft?.signature && (
                            <div className="mb-4 p-3 bg-black border border-gray-800">
                              <div className="text-xs text-gray-500 mb-1">{t('marketplace.listing.signature')}</div>
                              <div className="text-sm text-gray-300 italic truncate">
                                {nft.signature}
                              </div>
                            </div>
                          )}

                          <div className="divider"></div>

                          {/* Price and payment method */}
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-gray-500 text-sm">{t('marketplace.listing.price')}</span>
                              <span className="flex items-center gap-2">
                                <span className="text-2xl">
                                  {order.paymentMethod === 'USDT' ? '' : ''}
                                </span>
                                <span className="text-2xl font-bold font-mono">
                                  {order.price}
                                </span>
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">{t('marketplace.listing.paymentMethod')}</span>
                              <span className="font-mono">{order.paymentMethod}</span>
                            </div>
                          </div>

                          {/* Seller info */}
                          <div className="mb-4 text-xs text-gray-500">
                            {t('marketplace.listing.seller')}: {order.seller.slice(0, 6)}...{order.seller.slice(-4)}
                            {isMyOrder && <span className="ml-2 text-white">{t('marketplace.listing.mine')}</span>}
                          </div>

                          {/* Buy/Cancel button */}
                          {isMyOrder ? (
                            <button
                              onClick={() => handleCancel(order.orderId)}
                              disabled={processing}
                              className="btn-outline w-full py-3"
                            >
                              {t('marketplace.listing.cancelOrder')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBuy(order)}
                              disabled={processing}
                              className="btn-primary w-full py-3"
                            >
                              {processing ? t('marketplace.listing.buying') : t('marketplace.listing.buy')}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* My orders */}
            {selectedTab === 'my-orders' && (
              <div>
                {/* NFTs available for listing */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold mb-4">{t('marketplace.myNFTs.title')}</h3>
                  {myNFTs.length === 0 ? (
                    <div className="text-center py-10 card">
                      <p className="text-gray-400">{t('marketplace.myNFTs.empty')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {myNFTs.map((nft) => {
                        // Check if already listed
                        const isListed = myOrders.some(o => o.tokenId === nft.tokenId);

                        return (
                          <div key={nft.tokenId} className="card p-4 text-center">
                            <div className="text-xs text-gray-500 mb-1">NFT #{nft.tokenId}</div>
                            <div className="text-3xl font-black font-mono mb-2">
                              {nft.number}
                            </div>
                            {isListed ? (
                              <div className="text-xs text-gray-500 py-2">{t('marketplace.listing.listed')}</div>
                            ) : (
                              <button
                                onClick={() => handleCreateOrder(nft)}
                                className="btn-outline w-full py-2 text-sm"
                              >
                                {t('marketplace.listing.listForSale')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* My listings */}
                <div>
                  <h3 className="text-2xl font-bold mb-4">{t('marketplace.myListings.title')}</h3>
                  {myOrders.length === 0 ? (
                    <div className="text-center py-20">
                      <h3 className="text-2xl font-bold mb-4">{t('marketplace.myListings.empty')}</h3>
                      <p className="text-gray-400">{t('marketplace.myListings.emptyDesc')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {myOrders.map((order) => {
                        const nft = getNFTDetails(order.tokenId);

                        return (
                          <div key={order.orderId} className="card p-6 flex items-center justify-between">
                            <div className="flex items-center gap-6">
                              <div className="text-center">
                                <div className="text-xs text-gray-500 mb-1">{t('marketplace.myListings.nft')}</div>
                                <div className="text-2xl font-black font-mono">
                                  {nft?.number || '------'}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">{t('marketplace.listing.price')}</div>
                                <div className="text-xl font-bold font-mono">
                                  {order.price} {order.paymentMethod}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleCancel(order.orderId)}
                              disabled={processing}
                              className="btn-outline px-6 py-2"
                            >
                              {t('marketplace.myListings.cancel')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent trade records */}
            {selectedTab === 'trades' && (
              <div>
                {recentTrades.length === 0 ? (
                  <div className="text-center py-20">
                    <h3 className="text-2xl font-bold mb-4">{t('marketplace.trades.empty')}</h3>
                    <p className="text-gray-400">{t('marketplace.trades.emptyDesc')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentTrades.map((trade, index) => {
                      const nft = getNFTDetails(trade.tokenId);
                      const tradeTime = new Date(parseInt(trade.timestamp) * 1000);

                      return (
                        <div key={`${trade.orderId}-${index}`} className="card p-6">
                          <div className="flex items-center justify-between">
                            {/* Left: NFT info */}
                            <div className="flex items-center gap-6">
                              <div className="text-center">
                                <div className="text-xs text-gray-500 mb-1">NFT #{trade.tokenId}</div>
                                <div className="text-3xl font-black font-mono">
                                  {nft?.number || '------'}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">{t('marketplace.trades.price')}</div>
                                <div className="text-xl font-bold font-mono">
                                  {trade.price} {trade.paymentMethod}
                                </div>
                              </div>
                            </div>

                            {/* Middle: Trade parties */}
                            <div className="flex items-center gap-4 text-sm">
                              <div className="text-right">
                                <div className="text-gray-500">{t('marketplace.trades.seller')}</div>
                                <div className="font-mono">
                                  {trade.seller.slice(0, 6)}...{trade.seller.slice(-4)}
                                </div>
                              </div>
                              <div className="text-gray-500">→</div>
                              <div>
                                <div className="text-gray-500">{t('marketplace.trades.buyer')}</div>
                                <div className="font-mono">
                                  {trade.buyer.slice(0, 6)}...{trade.buyer.slice(-4)}
                                </div>
                              </div>
                            </div>

                            {/* Right: Time and fee */}
                            <div className="text-right">
                              <div className="text-sm text-gray-500">
                                {tradeTime.toLocaleDateString()} {tradeTime.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div className="text-xs text-gray-600">
                                {t('marketplace.trades.fee')}: {trade.platformFee} {trade.paymentMethod}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create order Modal */}
      {showCreateModal && selectedNFT && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="card max-w-md w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-3xl font-bold mb-6">{t('marketplace.listing.listForSale')}</h3>

            {/* NFT info */}
            <div className="text-center mb-6 p-4 bg-gray-950 border-2 border-gray-800">
              <div className="text-sm text-gray-500 mb-2">NFT #{selectedNFT.tokenId}</div>
              <div className="text-5xl font-black font-mono tracking-wider">
                {selectedNFT.number}
              </div>
              {selectedNFT.signature && (
                <div className="mt-3 text-sm text-gray-400 italic">
                  "{selectedNFT.signature}"
                </div>
              )}
            </div>

            {/* Payment method selection */}
            <div className="mb-6">
              <label className="block text-sm font-bold mb-3">{t('marketplace.listing.paymentMethod')}</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setOrderPaymentMethod('USDT')}
                  className={`p-4 border-2 transition-all ${
                    orderPaymentMethod === 'USDT'
                      ? 'border-white bg-white text-black'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="font-bold">USDT</div>
                </button>
                <button
                  onClick={() => setOrderPaymentMethod('ETH')}
                  className={`p-4 border-2 transition-all ${
                    orderPaymentMethod === 'ETH'
                      ? 'border-white bg-white text-black'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                >
                  <div className="font-bold">ETH</div>
                </button>
              </div>
            </div>

            {/* Price input */}
            <div className="mb-6">
              <label className="block text-sm font-bold mb-3">
                Price ({orderPaymentMethod})
              </label>
              <input
                type="number"
                value={orderPrice}
                onChange={(e) => setOrderPrice(e.target.value)}
                placeholder="0.00"
                className="input text-2xl font-mono"
                step="0.01"
                min="0"
              />
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleSubmitOrder}
                disabled={processing || !orderPrice}
                className="btn-primary w-full py-3"
              >
                {processing ? t('marketplace.sell.creating') : t('marketplace.sell.confirm')}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-outline w-full py-3"
              >
                {t('marketplace.sell.cancel')}
              </button>
            </div>

            <div className="mt-4 text-xs text-gray-500 text-center">
              {t('marketplace.sell.fee')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceV3;
